import { useState } from "react";
import type { SaidaRota } from "../../lib/trpc.js";
import { trpc } from "../../lib/trpc.js";
import { baixar } from "../../lib/api.js";
import { Botao, Chave, Falha, Selo, cx } from "../../components/ui.js";
import { podePedirOEnvio, rotuloDoEnvio, seloDoEnvio } from "../geracao/estado-do-envio.js";

type Card = SaidaRota["generations"]["listCapturas"]["cards"][number];
type Imagem = Card["imagens"][number];

const ROTULO_VERSAO: Record<string, string> = {
  gerada: "Imagem IA",
  composta: "Com capa",
  publicada: "Publicada",
};

/** O botão diz o que vai sair, porque o que vai é o que está na tela. */
const ROTULO_ENVIO: Record<string, string> = {
  gerada: "Enviar imagem da IA",
  composta: "Enviar montagem",
  publicada: "Enviar no WhatsApp",
};

/** O envio de uma imagem, como a tela do envio o lê. */
function envioDa(i: Imagem) {
  return {
    estado: i.envio.estado,
    motivo: i.envio.motivo,
    enviadaEm: i.enviadaEm,
    enviadaPara: i.enviadaPara,
  };
}

/**
 * O card aberto: a foto original e o que nasceu dela.
 *
 * Aqui é o único lugar do sistema onde as três versões convivem — a foto tirada,
 * a imagem da IA e a montagem publicada. Na projeção da TV só passa a montagem.
 */
export function DetalheCaptura({ card, aoFechar }: { card: Card; aoFechar: () => void }) {
  const utils = trpc.useUtils();
  /**
   * O que está na tela: a foto original, ou uma versão de uma das imagens.
   *
   * "Versão" porque uma imagem tem até três — a saída da IA, a montagem com a
   * capa e a publicada. Mostrar só a mais adiantada escondia justamente a
   * imagem de IA, que é o que o operador quer comparar.
   */
  const primeira = card.imagens.find((i) => i.versoes.length > 0);
  const [escolhida, setEscolhida] = useState<{ id: number; versao: string } | "foto">(
    primeira
      ? { id: primeira.id, versao: primeira.versoes[primeira.versoes.length - 1]!.nome }
      : "foto",
  );
  const [erro, setErro] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<"imagem" | "card" | null>(null);

  const recarregar = () => utils.generations.listCapturas.invalidate();

  /**
   * Põe a foto na fila do WhatsApp da SER (#105). O ritmo é da fila — uma por vez, 3 s entre mensagens —, e o card
   * passa a dizer "na fila" até sair; a Biblioteca reconsulta enquanto houver foto na fila.
   */
  const enviar = trpc.whatsapp.sendGeneration.useMutation({
    onSuccess: () => void recarregar(),
    onError: (e) => setErro(e.message),
  });

  const regerar = trpc.generations.regenerate.useMutation({
    onSuccess: () => void recarregar(),
    onError: (e) => setErro(e.message),
  });
  // Sem confirmação: não apaga nada, e publicar de novo desfaz.
  const despublicar = trpc.generations.despublicar.useMutation({
    onSuccess: (_r, v) => {
      // A versão "publicada" deixou de existir: sem trocar, o detalhe cairia na
      // foto original. O arquivo agora aparece como "composta".
      setEscolhida({ id: v.ids[0]!, versao: "composta" });
      void recarregar();
    },
    onError: (e) => setErro(e.message),
  });
  /**
   * Publicar a imagem aprovada que não está publicada — a despublicada (o arquivo ficou: `despublicar` o guarda
   * como composta) ou a aprovada num atendimento que parou antes da entrega. Sem isto, a foto despublicada não
   * tinha volta pela Biblioteca, e a salva sem IA (#95), que não tem "Gerar outra", ficava sem caminho nenhum.
   */
  const publicar = trpc.generations.finalize.useMutation({
    onSuccess: (_r, v) => {
      setEscolhida({ id: v.id, versao: "publicada" });
      void recarregar();
    },
    onError: (e) => setErro(e.message),
  });
  // A marca da galeria do salão (#96): tira da TV sem despublicar.
  const mostrarNaGaleria = trpc.generations.mostrarNaGaleria.useMutation({
    onSuccess: () => void recarregar(),
    onError: (e) => setErro(e.message),
  });
  const excluirImagem = trpc.generations.delete.useMutation({
    onSuccess: () => {
      setConfirmando(null);
      setEscolhida("foto");
      void recarregar();
    },
    onError: (e) => setErro(e.message),
  });
  const excluirCard = trpc.generations.deleteCaptura.useMutation({
    onSuccess: () => {
      void recarregar();
      aoFechar();
    },
    onError: (e) => setErro(e.message),
  });

  const imagem: Imagem | undefined =
    escolhida === "foto" ? undefined : card.imagens.find((i) => i.id === escolhida.id);
  const versao =
    escolhida === "foto" ? undefined : imagem?.versoes.find((v) => v.nome === escolhida.versao);
  const url = versao?.url ?? card.fotoOriginal;

  /**
   * O que o WhatsApp recebe é o que está na tela.
   *
   * Antes ia sempre a publicada, mesmo com a imagem de IA selecionada — o
   * operador escolhia uma coisa e a pessoa recebia outra. A foto original não é
   * enviável por aqui: o botão só existe para imagem publicada.
   */
  const versaoEnviada = (versao?.nome ?? "publicada") as "gerada" | "composta" | "publicada";
  const ocupado =
    regerar.isPending ||
    despublicar.isPending ||
    publicar.isPending ||
    mostrarNaGaleria.isPending ||
    excluirImagem.isPending ||
    excluirCard.isPending;
  // A foto salva sem IA não tem pedido de onde gerar outra imagem (#95).
  const soFotografia =
    card.imagens.length > 0 && card.imagens.every((i) => i.origem === "fotografia");

  return (
    <>
      <header className="flex items-start justify-between gap-3 border-b border-borda px-4 py-3">
        <div className="min-w-0">
          <h2 className="truncate font-display text-[1.125rem] leading-tight">
            {card.spectatorName}
          </h2>
          <p className="truncate text-[0.8125rem] text-texto-fraco">
            {/* A foto sem vivência pode ter Local (#101): "Sem vivência — Casa". */}
            {[card.vivencia?.nome ?? "Sem vivência", card.instituicao?.nome]
              .filter(Boolean)
              .join(" — ")}
          </p>
        </div>
        <button
          onClick={aoFechar}
          aria-label="Fechar"
          className="shrink-0 rounded-peca px-3 py-2 text-texto-suave transition-colors hover:bg-superficie hover:text-texto"
        >
          ✕
        </button>
      </header>

      <div className="flex min-h-0 flex-1 items-center justify-center bg-black p-2">
        <img src={url} alt={card.spectatorName} className="max-h-[55vh] w-auto object-contain" />
      </div>

      {/* A foto original é sempre a primeira: ela ancora o card e nunca sai. */}
      <div className="flex gap-2 overflow-x-auto border-t border-borda px-4 py-3">
        <Miniatura
          url={card.fotoOriginal}
          rotulo="Foto"
          ativa={escolhida === "foto"}
          onClick={() => setEscolhida("foto")}
        />
        {card.imagens.map((i, n) =>
          i.versoes.map((v) => (
            <Miniatura
              key={`${i.id}-${v.nome}`}
              url={v.mini}
              rotulo={
                // A foto sem IA despublicada aparece como "composta" (o arquivo guardado), mas não tem capa.
                v.nome === "composta" && i.origem === "fotografia"
                  ? "Foto salva"
                  : (ROTULO_VERSAO[v.nome] ?? v.nome)
              }
              sufixo={card.imagens.length > 1 ? String(card.imagens.length - n) : undefined}
              ativa={escolhida !== "foto" && escolhida.id === i.id && escolhida.versao === v.nome}
              onClick={() => setEscolhida({ id: i.id, versao: v.nome })}
            />
          )),
        )}
      </div>

      <div className="flex flex-col gap-3 border-t border-borda px-4 py-3">
        {imagem && (
          <div className="flex flex-wrap items-center gap-1.5">
            {imagem.publicada && imagem.naGaleria && <Selo tom="fixado">na galeria</Selo>}
            {imagem.publicada && !imagem.naGaleria && <Selo tom="quieto">fora da galeria</Selo>}
            {imagem.origem === "fotografia" && <Selo tom="quieto">só fotografia</Selo>}
            {imagem.status === "processing" && <Selo tom="projecao">gerando</Selo>}
            {imagem.status === "failed" && <Selo tom="descartar">falhou</Selo>}
            {!imagem.publicada && imagem.aprovada && <Selo tom="quieto">aprovada</Selo>}
            {seloDoEnvio(envioDa(imagem)) && (
              <Selo tom={seloDoEnvio(envioDa(imagem))!.tom}>
                {seloDoEnvio(envioDa(imagem))!.texto}
              </Selo>
            )}
          </div>
        )}

        {erro && <Falha mensagem={erro} />}
        {/* Não enviada: o que impediu. Na fila com motivo: a nova tentativa a caminho, e quando. */}
        {imagem?.envio.motivo && imagem.envio.estado === "nao_enviada" && (
          <Falha mensagem={imagem.envio.motivo} />
        )}
        {imagem?.envio.motivo && imagem.envio.estado === "na_fila" && (
          <p className="text-[0.8125rem] text-texto-suave">{imagem.envio.motivo}</p>
        )}

        {imagem?.publicada && confirmando === null && (
          <Chave
            ligada={imagem.naGaleria}
            aoAlternar={(mostrar) => {
              setErro(null);
              mostrarNaGaleria.mutate({ ids: [imagem.id], mostrar });
            }}
            disabled={ocupado}
            rotulo="Mostrar na galeria do salão"
            texto="Mostrar na galeria do salão"
          />
        )}

        {confirmando === "imagem" && (
          <Confirmacao
            pergunta={
              card.imagens.length > 1
                ? "Excluir esta imagem? As outras do card continuam."
                : "É a única imagem do card. A foto original fica, e o card volta a ficar sem imagem."
            }
            aoConfirmar={() => imagem && excluirImagem.mutate({ id: imagem.id })}
            aoCancelar={() => setConfirmando(null)}
            carregando={excluirImagem.isPending}
          />
        )}

        {confirmando === "card" && (
          <Confirmacao
            pergunta={`Excluir o card inteiro? Vão junto a foto original e ${card.imagens.length} ${
              card.imagens.length === 1 ? "imagem" : "imagens"
            }.`}
            aoConfirmar={() => excluirCard.mutate({ capturaId: card.capturaId })}
            aoCancelar={() => setConfirmando(null)}
            carregando={excluirCard.isPending}
          />
        )}

        {confirmando === null && (
          <div className="flex flex-wrap gap-2">
            {!soFotografia && (
              <Botao
                tom="quieto"
                disabled={ocupado}
                carregando={regerar.isPending}
                onClick={() => {
                  setErro(null);
                  regerar.mutate({ id: card.imagens[0]?.id ?? 0 });
                }}
              >
                Gerar outra imagem
              </Botao>
            )}

            {imagem?.publicada && (
              <>
                <Botao
                  tom="brasa"
                  disabled={ocupado || !podePedirOEnvio(envioDa(imagem))}
                  carregando={enviar.isPending}
                  onClick={() => {
                    setErro(null);
                    // A foto vai sempre para a pessoa: o grupo do Local saiu no #105 e não voltou no #107.
                    enviar.mutate({ generationId: imagem.id, versao: versaoEnviada });
                  }}
                >
                  {rotuloDoEnvio(envioDa(imagem), ROTULO_ENVIO[versaoEnviada])}
                </Botao>
                <Botao
                  tom="quieto"
                  disabled={ocupado}
                  onClick={() => baixar(`/api/generations/export?ids=${imagem.id}`)}
                >
                  Baixar
                </Botao>
                <Botao
                  tom="quieto"
                  disabled={ocupado}
                  carregando={despublicar.isPending}
                  onClick={() => {
                    setErro(null);
                    despublicar.mutate({ ids: [imagem.id] });
                  }}
                >
                  Despublicar
                </Botao>
              </>
            )}

            {imagem && imagem.aprovada && !imagem.publicada && imagem.versoes.length > 0 && (
              <Botao
                tom="quieto"
                disabled={ocupado}
                carregando={publicar.isPending}
                onClick={() => {
                  setErro(null);
                  publicar.mutate({ id: imagem.id });
                }}
              >
                Publicar
              </Botao>
            )}

            {imagem && (
              <Botao tom="quieto" disabled={ocupado} onClick={() => setConfirmando("imagem")}>
                Excluir imagem
              </Botao>
            )}

            <Botao tom="descartar" disabled={ocupado} onClick={() => setConfirmando("card")}>
              Excluir card
            </Botao>
          </div>
        )}
      </div>
    </>
  );
}

function Miniatura({
  url,
  rotulo,
  sufixo,
  ativa,
  onClick,
}: {
  url: string;
  rotulo: string;
  /** Numera quando o card tem mais de uma imagem; some quando só há uma. */
  sufixo?: string;
  ativa: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={ativa}
      className={cx(
        "shrink-0 overflow-hidden rounded-peca border-2 transition-colors",
        ativa ? "border-brasa" : "border-borda hover:border-borda-forte",
      )}
    >
      <img src={url} alt="" className="size-16 object-cover" />
      <span className="block bg-superficie px-2 py-1 text-[0.6875rem] text-texto-suave">
        {sufixo ? `${rotulo} ${sufixo}` : rotulo}
      </span>
    </button>
  );
}

/** Confirmação inline, como no resto do sistema — sem sobreposição sobre sobreposição. */
function Confirmacao({
  pergunta,
  aoConfirmar,
  aoCancelar,
  carregando,
}: {
  pergunta: string;
  aoConfirmar: () => void;
  aoCancelar: () => void;
  carregando: boolean;
}) {
  return (
    <div className="rounded-peca border border-borda bg-superficie p-3">
      <p className="text-[0.875rem] text-texto">{pergunta}</p>
      <div className="mt-3 flex gap-2">
        <Botao tom="descartar" onClick={aoConfirmar} carregando={carregando}>
          Excluir
        </Botao>
        <Botao tom="quieto" onClick={aoCancelar} disabled={carregando}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
